import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { artworks } from '../data/products';
import { useCart } from '../context/CartContext';
import { addProductToCart } from '../utils/cart';
import ProductGallery from '../components/product/ProductGallery';
import Accordion, { AccordionItem } from '../components/ui/Accordion';
import InquiryModal from '../components/ui/InquiryModal';
import SEO from '../components/seo/SEO';
import './ProductPage.css';
import './ArtworkProductPage.css';

const ArtworkProductPage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const { addItem } = useCart();
  const artwork = artworks.find((a) => a.handle === handle);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  if (!artwork) return <Navigate to="/art" replace />;

  const isOriginal = artwork.type === 'Original';
  const variant = artwork.variants?.[selectedVariant];

  const seoDescription = `${artwork.title} — ${artwork.type === 'Original' ? 'Original artwork' : 'Fine art print'} by Micah Lynise. ${artwork.medium || ''}`.trim();


  const handleAddToCart = async () => {
    if (!variant) return;
    
    setIsAdding(true);
    
    try {
      addProductToCart(
        {
          productId: artwork.id,
          handle: artwork.handle,
          title: artwork.title,
          image: artwork.images[0],
          variant,
          quantity: 1,
        },
        addItem
      );
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="product-page">
      <SEO
        title={artwork.title}
        description={seoDescription}
        path={`/art/${artwork.handle}`}
        image={artwork.images[0]}
        type="product"
        product={{
          name: artwork.title,
          description: seoDescription,
          image: artwork.images[0],
          priceCents: variant?.priceCents || 3500,
          category: 'Art',
          sku: artwork.id,
        }}
      />
      <nav className="product-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/art">Art</Link>
        <span aria-hidden="true"> / </span>
        <span>{artwork.title}</span>
      </nav>

      <div className="product-page__layout">
        <div className="product-page__gallery-col">
          <ProductGallery images={artwork.images} title={artwork.title} />
        </div>

        <div className="product-page__info-col artwork-info">
          <div className="artwork-info__header">
            <h1 className="artwork-info__title">{artwork.title}</h1>
            {!isOriginal && (
              <p className="artwork-info__price">
                {variant ? variant.priceDisplay : artwork.price}
              </p>
            )}
          </div>

          {/* Metadata */}
          <dl className="artwork-info__meta">
            <div className="artwork-info__meta-row">
              <dt>Type</dt>
              <dd>{isOriginal ? 'Original Painting' : 'Fine Art Print'}</dd>
            </div>
            {artwork.medium && (
              <div className="artwork-info__meta-row">
                <dt>Medium</dt>
                <dd>{artwork.medium}</dd>
              </div>
            )}
            {artwork.dimensions && (
              <div className="artwork-info__meta-row">
                <dt>Dimensions</dt>
                <dd>{artwork.dimensions}</dd>
              </div>
            )}
            {artwork.year && (
              <div className="artwork-info__meta-row">
                <dt>Year</dt>
                <dd>{artwork.year}</dd>
              </div>
            )}
            <div className="artwork-info__meta-row">
              <dt>Availability</dt>
              <dd>{artwork.available ? 'Available' : 'Sold'}</dd>
            </div>
          </dl>

          {/* Story */}
          {artwork.story && (
            <p className="artwork-info__story">{artwork.story}</p>
          )}

          {/* Print sizes (if print with variants) */}
          {!isOriginal && artwork.variants && artwork.variants.length > 0 && (
            <div className="artwork-info__sizes">
              <p className="artwork-info__section-label">Print Size</p>
              <div className="artwork-info__size-options">
                {artwork.variants.map((v, idx) => (
                  <button
                    key={v.value}
                    className={`artwork-info__size-btn ${selectedVariant === idx ? 'artwork-info__size-btn--active' : ''}`}
                    onClick={() => setSelectedVariant(idx)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          {artwork.available ? (
            isOriginal ? (
              <button className="artwork-info__inquiry" onClick={() => setInquiryOpen(true)}>
                Inquire
              </button>
            ) : (
              <button 
                className="artwork-info__atc"
                onClick={handleAddToCart}
                disabled={isAdding}
                aria-busy={isAdding}
              >
                {isAdding ? 'Adding...' : 'Add to Cart'}
              </button>
            )
          ) : (
            <p className="artwork-info__sold">This work has been sold.</p>
          )}

          {isOriginal && (
            <p className="artwork-info__inquiry-note">
              Contact me for pricing and availability.
            </p>
          )}

          {/* Accordion */}
          <Accordion>
            <AccordionItem title="Shipping & Framing">
              <p>Original works ship carefully packed within 3–5 business days. Prints ship within 1–3 business days.</p>
            </AccordionItem>
            <AccordionItem title="About This Work">
              <p>{artwork.story ?? 'A piece created from observation, memory, and light.'}</p>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {isOriginal && (
        <InquiryModal
          isOpen={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          artworkTitle={artwork.title}
          artworkImage={artwork.images[0]}
        />
      )}
    </div>
  );
};

export default ArtworkProductPage;
